import { Layout } from '@/components/Layout.tsx';
import { SITE } from '@/data/site.ts';

/**
 * Privacy Policy / Terms & Conditions / Autopay Terms.
 *
 * One page with three anchor-linked sections, matching the original and the
 * footer links that target #privacy-policy, #terms-and-conditions, #autopay.
 *
 * The nav uses the `onLight` variant because this page has no dark hero image.
 * Headings are h2 (section) / h3 (clause) beneath the page h1, so the outline
 * is sequential for screen-reader navigation.
 */
export function Policies() {
  return (
    <Layout currentPath="/policies" navOnLight>
      <div className="article-hero">
        <div className="container">
          <h1 className="fade-in">Policies</h1>
          <p className="lead fade-in fade-in-delay-1">
            Our Privacy Policy, Terms &amp; Conditions, and Autopay Terms of
            Service.
          </p>
        </div>
      </div>

      <div className="container">
        <div className="article-body" style={{ maxWidth: '800px' }}>
          {/* ===== Privacy Policy ===== */}
          <h2 id="privacy-policy">Privacy Policy</h2>
          <p>
            <em>Effective Date: January 14, 2025</em>
            <br />
            Company: Lux Financial
            <br />
            Website: luxfinancial.io
          </p>

          <h3>1. Introduction</h3>
          <p>
            At Lux Financial, your privacy is important to us. This Privacy
            Policy explains how we collect, use, and protect the personal
            information you provide through our website, luxfinancial.io. By
            using our website and submitting your information through any
            contact or lead form, you agree to the terms outlined in this
            policy.
          </p>

          <h3>2. Information We Collect</h3>
          <p>
            When you complete a form on our website, we may collect the
            following personal information:
          </p>
          <ul>
            <li>Your full name</li>
            <li>Phone number</li>
            <li>Email address</li>
            <li>
              Any additional information you provide in your message or form
              submission
            </li>
          </ul>
          <p>
            We collect this information only when you voluntarily submit it to
            us through our contact form.
          </p>

          <h3>3. How We Use Your Information</h3>
          <p>The information you provide is used for the following purposes:</p>
          <ul>
            <li>
              To contact you via phone, text message, or email regarding the
              services you have requested or expressed interest in.
            </li>
            <li>
              To provide you with information, updates, or assistance related to
              Lux Financial&rsquo;s services.
            </li>
            <li>To improve our communication and customer experience.</li>
          </ul>
          <p>
            We do not sell, rent, or trade your personal information to any
            third-party companies.
          </p>

          <h3>4. Communication Consent</h3>
          <p>
            By submitting your information through our website, you consent to
            being contacted by Lux Financial via:
          </p>
          <ul>
            <li>Phone call</li>
            <li>Text message (SMS/MMS)</li>
            <li>Email</li>
          </ul>
          <p>
            Message and data rates may apply. You can opt out of future
            communications at any time by replying &ldquo;STOP&rdquo; to a text
            message or contacting us directly using the information below.
          </p>

          <h3>5. Data Protection and Security</h3>
          <p>
            We take reasonable measures to safeguard your personal information
            from unauthorized access, use, or disclosure. While we implement
            industry-standard security practices, no online platform can
            guarantee complete security.
          </p>

          <h3>6. Sharing of Information</h3>
          <p>
            We do not sell or share your personal information with third
            parties. We may, however, share limited data with trusted service
            providers who assist us in operating our website or communicating
            with customers (for example, SMS or email platforms). These partners
            are required to protect your information and use it solely for the
            purpose of providing their contracted services.
          </p>

          <h3>7. Your Rights and Choices</h3>
          <p>You have the right to:</p>
          <ul>
            <li>
              Request access to the personal information we hold about you.
            </li>
            <li>Request correction or deletion of your data.</li>
            <li>Withdraw consent for communication at any time.</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the information
            below.
          </p>

          <h3>8. Third-Party Links</h3>
          <p>
            Our website may include links to other websites. Lux Financial is
            not responsible for the content, privacy practices, or security of
            those third-party sites.
          </p>

          <h3>9. Updates to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in our practices or applicable laws. Updates will be posted
            on this page with a revised effective date.
          </p>

          <h3>10. Contact Us</h3>
          <p>
            If you have any questions about this Privacy Policy or how your
            information is used, please contact us.
          </p>

          <div className="divider" />

          {/* ===== Terms & Conditions ===== */}
          <h2 id="terms-and-conditions">Terms &amp; Conditions</h2>
          <p>
            <em>Effective Date: October 9, 2025</em>
            <br />
            Company: Lux Financial
            <br />
            Website: luxfinancial.io
          </p>

          <h3>1. Acceptance of Terms</h3>
          <p>
            By accessing or using this website, luxfinancial.io, you agree to
            comply with and be bound by these Terms &amp; Conditions and our
            Privacy Policy. If you do not agree with any part of these terms,
            you should not use this website or submit any personal information
            through it.
          </p>

          <h3>2. Purpose of Website</h3>
          <p>
            The information and materials provided on this site are for general
            informational purposes only. Lux Financial provides services related
            to financial consulting and investment strategy education. The
            content on this website should not be considered professional
            financial or legal advice. You should consult with a qualified
            advisor before making any financial decisions.
          </p>

          <h3>3. Use of Information</h3>
          <p>
            By submitting your information through any contact form on our
            website, you acknowledge and agree that:
          </p>
          <ul>
            <li>
              Lux Financial may contact you via phone, text message (SMS/MMS),
              or email to provide information related to your inquiry or the
              services you requested.
            </li>
            <li>Message and data rates may apply for SMS communications.</li>
            <li>
              You can opt out of communications at any time by replying
              &ldquo;STOP&rdquo; to text messages or contacting us directly.
            </li>
          </ul>

          <h3>4. No Guarantee of Results</h3>
          <p>
            While Lux Financial aims to provide accurate information and
            high-quality service, we do not guarantee specific financial results
            or outcomes. All investment or financial decisions carry risk, and
            past performance does not guarantee future returns.
          </p>

          <h3>5. Intellectual Property Rights</h3>
          <p>
            All content on this website — including but not limited to text,
            images, graphics, logos, videos, and design — is the property of Lux
            Financial or its licensors and is protected by copyright and
            trademark laws. You may not copy, reproduce, modify, or distribute
            any material from this site without prior written consent from Lux
            Financial.
          </p>

          <h3>6. Limitation of Liability</h3>
          <p>
            Lux Financial is not responsible for any direct, indirect,
            incidental, or consequential damages that may result from the use of
            or inability to use our website or services. This includes, but is
            not limited to, damages resulting from errors, omissions,
            interruptions, or loss of data or profits.
          </p>

          <h3>7. Third-Party Links and Services</h3>
          <p>
            Our website may include links to third-party websites or services.
            These links are provided for convenience only. Lux Financial has no
            control over, and assumes no responsibility for, the content,
            privacy policies, or practices of any third-party sites or services.
          </p>

          <h3>8. Privacy</h3>
          <p>
            Your submission of personal information through our website is
            governed by our Privacy Policy. By using this website, you consent
            to the collection and use of your information as described in that
            policy.
          </p>

          <h3>9. Changes to These Terms</h3>
          <p>
            Lux Financial may update or modify these Terms &amp; Conditions at
            any time without prior notice. Any changes will be posted on this
            page with an updated effective date. Your continued use of the
            website after any modifications constitutes acceptance of those
            changes.
          </p>

          <h3>10. Governing Law</h3>
          <p>
            These Terms &amp; Conditions are governed by and construed in
            accordance with the laws of the State of Utah, without regard to its
            conflict of law principles.
          </p>

          <h3>11. Contact Us</h3>
          <p>
            If you have any questions regarding these Terms &amp; Conditions,
            please contact us.
          </p>

          <div className="divider" />

          {/* ===== Autopay ===== */}
          <h2 id="autopay">Autopay Terms of Service</h2>
          <p>
            By using Auto Pay, you agree to the following terms and conditions:
          </p>

          <h3>Enrollment in Auto Pay</h3>
          <p>
            When you enroll in Auto Pay, you authorize Lux Financial LLC to
            automatically charge your selected payment method for recurring
            charges, including any subscription fees or ongoing services, on the
            due date.
          </p>

          <h3>Payment Methods</h3>
          <p>
            You can link a valid credit or debit card, or a bank account, to
            your Auto Pay account. It is your responsibility to ensure your
            payment information is up-to-date. You can update your payment
            method anytime through your account or by contacting us.
          </p>

          <h3>Billing and Payment</h3>
          <p>
            Payments will be automatically processed according to your billing
            cycle or service period. You will be charged the amount due for
            services as specified in your plan. If there are any changes in the
            payment amount (such as price adjustments), we will notify you in
            advance. If the payment is declined due to insufficient funds or
            incorrect payment details, you will be notified, and your service
            may be interrupted until payment is successfully processed.
          </p>

          <h3>Timing of Payments</h3>
          <p>
            Auto Pay payments will be debited on or shortly after the billing
            date, as specified in your service agreement. It is your
            responsibility to ensure your payment method has sufficient funds to
            cover the charge.
          </p>

          <h3>Cancelling Auto Pay</h3>
          <p>
            You can cancel Auto Pay at any time by logging into your account or
            contacting customer support. Please note that cancellations will
            take effect at the next billing cycle. Any unpaid balance will still
            be due before cancellation.
          </p>

          <h3>Refunds</h3>
          <p>
            If you believe you were charged incorrectly, please contact our
            support team within 15 days. Refund requests made after this period
            may not be eligible for processing. Any refunds will be issued to
            your original payment method.
          </p>

          <h3>Account and Payment Method Updates</h3>
          <p>
            You are responsible for keeping your payment details accurate. If
            your payment method expires or is canceled, or if there are any
            changes to your account, you must update your details immediately to
            avoid interruptions to your service.
          </p>

          <h3>Late Fees and Penalties</h3>
          <p>
            If a payment fails or is delayed, a late fee may be applied,
            depending on the terms of your service. Your account may also be
            temporarily suspended or terminated until any overdue payments are
            made.
          </p>

          <h3>Termination of Auto Pay</h3>
          <p>
            We reserve the right to suspend or terminate Auto Pay at our
            discretion, particularly in cases of non-payment, fraud, or
            violations of these terms.
          </p>

          <h3>Modifications</h3>
          <p>
            We may update these Auto Pay terms from time to time. You will be
            notified of any significant changes, and continued use of Auto Pay
            indicates your acceptance of any updated terms.
          </p>

          <div className="divider" />

          {/* ===== Contact ===== */}
          <h2 id="contact">Contact Us</h2>
          <p>Lux welcomes your questions or comments regarding the Terms:</p>
          <p>
            Lux Financial LLC
            <br />
            {SITE.address.street}
            <br />
            {SITE.address.locality}, Utah {SITE.address.postalCode}
          </p>
          <p>
            Email Address:
            <br />
            <a href="mailto:support@luxfinancial.io" className="inline-link">
              support@luxfinancial.io
            </a>
          </p>
          <p>
            Telephone number:
            <br />
            {/* Note: this differs from the main site phone number, as in the original. */}
            <a href="tel:+18772373401" className="inline-link">
              877-237-3401
            </a>
          </p>

          <div className="article-disclaimer">
            Effective as of January 01, 2023
          </div>
        </div>
      </div>
    </Layout>
  );
}
